import { OrderBig, LineItem, Variant } from '@chordcommerce/react-autonomy';
import { useMemo } from 'react';
import useProducts from '~/hooks/graphql/queries/use-products';

// Define a new type for the enhanced variant
export type EnhancedVariant = Omit<Variant, 'id' | 'optionValues'> &
  Omit<Queries.VariantBaseFragment, 'id' | 'optionValues'> & {
    id: string | number;
    optionValues: Queries.VariantBaseFragment['optionValues'];
    name: string;
    sku: string;
    slug: string;
  };

export type EnhancedBundleLineItem = LineItem & {
  isBundle: true;
  productName: string;
};

export type EnhancedProductLineItem = Omit<LineItem, 'variant'> & {
  isBundle: false;
  productName: string;
  product?: Queries.ProductBaseFragment;
  variant: EnhancedVariant;
};

export type EnhancedCartItem = EnhancedBundleLineItem | EnhancedProductLineItem;

export const useCartItems = (cart: Partial<OrderBig>): EnhancedCartItem[] | undefined => {
  const products = useProducts();
  const { lineItems } = cart;
  const cartItems = useMemo(
    () =>
      lineItems?.reduce<EnhancedCartItem[]>((acc, item) => {
        if (item.isBundle) {
          return [
            ...acc,
            {
              ...item,
              productName: item.variant.name ?? '',
              isBundle: true,
            } satisfies EnhancedBundleLineItem,
          ];
        } else {
          const product = products.find((p) => p.slug === item.variant.slug);
          const matchingVariant = product?.variants?.find((v) => v?.sku === item.variant.sku);

          // If no matching variant is found, log error and skip this item
          if (!matchingVariant) {
            console.error(
              `Matching variant not found for SKU: ${item.variant.sku}, Slug: ${item.variant.slug}. Skipping item.`
            );
            return acc;
          }

          const enhancedVariant: EnhancedVariant = {
            ...item.variant,
            ...matchingVariant,
            id: item.variant.id,
            name: item.variant.name,
            sku: item.variant.sku,
            slug: item.variant.slug,
            optionValues: matchingVariant?.optionValues ?? [],
          };

          return [
            ...acc,
            {
              ...item,
              isBundle: false,
              productName: product?.name ?? item.variant.name,
              variant: enhancedVariant,
              product,
            } satisfies EnhancedProductLineItem,
          ];
        }
      }, []),
    [products, lineItems]
  );

  return cartItems;
};
